import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import InspectionQueue from './components/InspectionQueue';
import InspectionDetailForm from './components/InspectionDetailForm';
import InspectionHistory from './components/InspectionHistory';
import QualityMetricsDashboard from './components/QualityMetricsDashboard';
import DefectTrackingChart from './components/DefectTrackingChart';
import InspectorPerformancePanel from './components/InspectorPerformancePanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const QualityControlAndInspectionCheckpoints = () => {
  const { projectId } = useParams();
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const mockCheckpoints = [
    {
      id: 1,
      productName: "Modular Wall Panel - Type A",
      batchNumber: "WP-2847",
      checkpointType: "Pre-Assembly Inspection",
      priority: "critical",
      status: "pending",
      dueDate: new Date(Date.now() + 2 * 60 * 60 * 1000),
      location: "Assembly Line 3 - Station 4",
      assignedInspector: "Sarah Chen"
    },
    {
      id: 2,
      productName: "Electrical Module Assembly",
      batchNumber: "EM-1923",
      checkpointType: "Functional Testing",
      priority: "high",
      status: "pending",
      dueDate: new Date(Date.now() + 4 * 60 * 60 * 1000),
      location: "Testing Bay 2",
      assignedInspector: "Michael Rodriguez"
    },
    {
      id: 3,
      productName: "Roof Truss Component",
      batchNumber: "RT-3456",
      checkpointType: "Dimensional Verification",
      priority: "medium",
      status: "pending",
      dueDate: new Date(Date.now() + 8 * 60 * 60 * 1000),
      location: "Fabrication Area B",
      assignedInspector: "Sarah Chen"
    },
    {
      id: 4,
      productName: "Window Frame Assembly",
      batchNumber: "WF-7821",
      checkpointType: "Surface Quality Check",
      priority: "high",
      status: "pending",
      dueDate: new Date(Date.now() + 3 * 60 * 60 * 1000),
      location: "Finishing Station 1",
      assignedInspector: "James Wilson"
    },
    {
      id: 5,
      productName: "Plumbing Module",
      batchNumber: "PM-5634",
      checkpointType: "Pressure Testing",
      priority: "critical",
      status: "pending",
      dueDate: new Date(Date.now() + 1 * 60 * 60 * 1000),
      location: "Testing Bay 1",
      assignedInspector: "Michael Rodriguez"
    },
    {
      id: 6,
      productName: "Floor Panel System",
      batchNumber: "FP-9012",
      checkpointType: "Load Bearing Test",
      priority: "medium",
      status: "pending",
      dueDate: new Date(Date.now() + 12 * 60 * 60 * 1000),
      location: "Structural Testing Area",
      assignedInspector: "Sarah Chen"
    }
  ];

  const mockHistory = [
    {
      id: 101,
      productName: "Modular Wall Panel - Type B",
      batchNumber: "WP-2846",
      checkpointType: "Final Inspection",
      status: "approved",
      inspector: "Sarah Chen",
      completedDate: new Date(Date.now() - 2 * 60 * 60 * 1000),
      defectCount: 0,
      notes: "All quality criteria met. Surface finish excellent. Dimensions within tolerance."
    },
    {
      id: 102,
      productName: "HVAC Duct Assembly",
      batchNumber: "HD-4521",
      checkpointType: "Airflow Testing",
      status: "rejected",
      inspector: "Michael Rodriguez",
      completedDate: new Date(Date.now() - 4 * 60 * 60 * 1000),
      defectCount: 2,
      notes: "Airflow restriction detected in section 3. Seam integrity compromised at joint B-4."
    },
    {
      id: 103,
      productName: "Electrical Panel Box",
      batchNumber: "EP-7834",
      checkpointType: "Safety Compliance",
      status: "approved",
      inspector: "James Wilson",
      completedDate: new Date(Date.now() - 6 * 60 * 60 * 1000),
      defectCount: 0,
      notes: "Passed all electrical safety tests. Grounding verified. Labeling complete."
    },
    {
      id: 104,
      productName: "Door Frame Unit",
      batchNumber: "DF-2109",
      checkpointType: "Fit and Finish",
      status: "approved",
      inspector: "Sarah Chen",
      completedDate: new Date(Date.now() - 8 * 60 * 60 * 1000),
      defectCount: 0,
      notes: "Perfect alignment. Hardware installation correct. Paint finish uniform."
    },
    {
      id: 105,
      productName: "Insulation Panel",
      batchNumber: "IP-6745",
      checkpointType: "Thermal Performance",
      status: "rejected",
      inspector: "Michael Rodriguez",
      completedDate: new Date(Date.now() - 10 * 60 * 60 * 1000),
      defectCount: 1,
      notes: "R-value below specification in corner section. Requires additional insulation layer."
    },
    {
      id: 106,
      productName: "Staircase Module",
      batchNumber: "SM-3892",
      checkpointType: "Structural Integrity",
      status: "approved",
      inspector: "James Wilson",
      completedDate: new Date(Date.now() - 12 * 60 * 60 * 1000),
      defectCount: 0,
      notes: "Load test passed. Welds inspected and approved. Handrail secure."
    }
  ];

  const mockMetrics = {
    passRate: 87.5,
    passRateChange: 3.2,
    avgInspectionTime: 24,
    inspectionTimeChange: -5.1,
    defectsFound: 142,
    defectsChange: -8.3,
    complianceScore: 94.2,
    complianceChange: 2.1
  };

  const mockDefectData = [
    { week: "Week 1", dimensional: 12, surface: 8, assembly: 5, functional: 3 },
    { week: "Week 2", dimensional: 15, surface: 6, assembly: 7, functional: 4 },
    { week: "Week 3", dimensional: 10, surface: 9, assembly: 4, functional: 2 },
    { week: "Week 4", dimensional: 8, surface: 7, assembly: 6, functional: 5 }
  ];

  const mockInspectors = [
    {
      name: "Sarah Chen",
      role: "Senior QC Inspector",
      completedToday: 18,
      approvalRate: 92,
      avgTime: 22,
      accuracy: 96
    },
    {
      name: "Michael Rodriguez",
      role: "QC Inspector",
      completedToday: 15,
      approvalRate: 88,
      avgTime: 26,
      accuracy: 94
    },
    {
      name: "James Wilson",
      role: "QC Inspector",
      completedToday: 12,
      approvalRate: 85,
      avgTime: 28,
      accuracy: 91
    }
  ];

  const handleSave = (formData) => {
    console.log('Saving inspection:', formData);
  };

  const handleApprove = (formData) => {
    console.log('Approving inspection:', formData);
    setSelectedCheckpoint(null);
  };

  const handleReject = (formData) => {
    console.log('Rejecting inspection:', formData);
    setSelectedCheckpoint(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} />

      <div style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
                  aria-label="Toggle sidebar"
                >
                  <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                </button>
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                    Quality Control & Inspection
                  </h1>
                  <p className="caption text-muted-foreground mt-1">
                    Manage quality checkpoints and compliance tracking
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 md:gap-3">
                <Button variant="outline" iconName="Filter" className="hidden md:flex">
                  Filter
                </Button>
                <Button variant="outline" iconName="Download" className="hidden sm:flex">
                  Export
                </Button>
                <NotificationCenter />
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <QualityMetricsDashboard metrics={mockMetrics} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DefectTrackingChart data={mockDefectData} />
            <InspectorPerformancePanel inspectors={mockInspectors} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
            <div className="lg:col-span-3">
              <div className="sticky top-24 h-[calc(100vh-8rem)]">
                <InspectionQueue
                  checkpoints={mockCheckpoints}
                  selectedCheckpoint={selectedCheckpoint}
                  onSelectCheckpoint={setSelectedCheckpoint}
                />
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="sticky top-24 h-[calc(100vh-8rem)]">
                <InspectionDetailForm
                  checkpoint={selectedCheckpoint}
                  onSave={handleSave}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="sticky top-24 h-[calc(100vh-8rem)]">
                <InspectionHistory history={mockHistory} />
              </div>
            </div>
          </div>
        </main>
      </div>

      <QuickActionsToolbar />
    </div>
  );
};

export default QualityControlAndInspectionCheckpoints;